import { Button, ButtonTypeMap } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import React, { PropsWithChildren, memo } from 'react';
import { Colors } from '../../design/color/Colors';

export type UiButtonStyle = 'outlined' | 'ghost' | 'primary' | 'secondary' | 'success' | 'danger' | 'light';
const LOADER_SIZES = {
  small: '1rem',
  medium: '1.25rem',
  large: '1.5rem',
};

type Props = {
  onClick?: () => void;
  pending?: boolean;
  contentClasses?: string;
  style?: UiButtonStyle;
  disabled?: boolean;
} & ButtonTypeMap['props'];

export const UiButton = memo(
  ({
    size = 'medium',
    style = 'primary',
    onClick,
    pending,
    children,
    contentClasses,
    disabled,
  }: PropsWithChildren<Props>) => {
    const classes = useStyles();
    const wrapperClasses = classNames(classes[style], classes.wrapperClasses);

    return (
      <Button disabled={disabled} size={size} className={wrapperClasses} onClick={onClick}>
        <div className={contentClasses}>{children}</div>
        {pending && <CircularProgress size={LOADER_SIZES[size]} />}
      </Button>
    );
  },
);

const useStyles = makeStyles(() => ({
  primary: {
    background: Colors.primary,
    color: Colors.textOnPrimary,
    '&:hover': {
      background: Colors.primaryHover,
      color: Colors.textOnPrimary,
    },
  },
  secondary: {
    background: Colors.withOpacity(Colors.textPrimary, 0.16),
    color: Colors.textPrimary,
    '&:hover': {
      background: Colors.withOpacity(Colors.textPrimary, 0.25),
    },
  },
  wrapperClasses: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
}));
